import graphql__b69d0545979ae00cf3e8 from "./__generated__/ExpenseInvoiceServicePeriodConfirmMutation.graphql.ts";
import graphql__0643960550842719252d from "./__generated__/ExpenseInvoiceServicePeriodCorrectMutation.graphql.ts";
import graphql__9da624292858941886bd from "./__generated__/ExpenseInvoiceServicePeriod_invoice.graphql.ts";import * as React from "react";
import {useEffect, useState} from "react";
import {graphql} from "react-relay";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faCheck} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import ActionButton from "./presentational/button/ActionButton";
import {ServicePeriodStatusBadge} from "./IncomingInvoicesView";
import {formatInvoiceMonth, formatServicePeriod} from "../helper/expenseInvoiceFormat";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../contexts/ErrorContext";
import type {ExpenseInvoiceServicePeriod_invoice$key} from "./__generated__/ExpenseInvoiceServicePeriod_invoice.graphql";
import type {ExpenseInvoiceServicePeriodConfirmMutation} from "./__generated__/ExpenseInvoiceServicePeriodConfirmMutation.graphql";
import type {ExpenseInvoiceServicePeriodCorrectMutation} from "./__generated__/ExpenseInvoiceServicePeriodCorrectMutation.graphql";

const confirmMutation = graphql__b69d0545979ae00cf3e8;

const correctMutation = graphql__0643960550842719252d;

type Props = {
    invoice: ExpenseInvoiceServicePeriod_invoice$key
};

/**
 * The service period of an incoming invoice: confirm the proposal or set the period by hand.
 * A period set by hand counts as confirmed. Without a period the invoice counts in its invoice
 * month, which is a valid default and needs no action.
 */
const ExpenseInvoiceServicePeriod: React.FC<Props> = (props) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const invoice = useFragment(
        graphql__9da624292858941886bd,
        props.invoice
    );

    const storedStart = invoice.servicePeriodStart?.slice(0, 10) ?? '';
    const storedEnd = invoice.servicePeriodEnd?.slice(0, 10) ?? '';
    const [start, setStart] = useState(storedStart);
    const [end, setEnd] = useState(storedEnd);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setStart(storedStart);
        setEnd(storedEnd);
    }, [storedStart, storedEnd]);

    const servicePeriod = formatServicePeriod(intl, invoice.servicePeriodStart, invoice.servicePeriodEnd);
    const isChanged = start !== storedStart || end !== storedEnd;
    const isValid = start !== '' && end !== '' && end >= start;

    const confirm = () => {
        setIsSubmitting(true);
        commitMutationWithErrorHandling<ExpenseInvoiceServicePeriodConfirmMutation>(
            relayEnvironment,
            {
                mutation: confirmMutation,
                variables: {input: {id: invoice.id}},
                onCompleted: () => setIsSubmitting(false),
                onError: () => setIsSubmitting(false),
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToConfirmServicePeriod'})}
        );
    };

    const correct = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) {
            return;
        }
        setIsSubmitting(true);
        commitMutationWithErrorHandling<ExpenseInvoiceServicePeriodCorrectMutation>(
            relayEnvironment,
            {
                mutation: correctMutation,
                variables: {input: {id: invoice.id, start, end}},
                onCompleted: () => setIsSubmitting(false),
                onError: () => setIsSubmitting(false),
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToCorrectServicePeriod'})}
        );
    };

    const dateInputClassName = 'border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-900 w-full';

    return (
        <Panel headline={intl.formatMessage({id: 'view.incomingInvoices.col.servicePeriod'})}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
                {servicePeriod === null ? (
                    <span className="text-gray-400" title={intl.formatMessage({id: 'expense.servicePeriod.invoiceMonth'})}>{formatInvoiceMonth(intl, invoice.invoiceDate)}</span>
                ) : (
                    <>
                        <span className="text-gray-900">{servicePeriod}</span>
                        {invoice.servicePeriodStatus && <ServicePeriodStatusBadge status={invoice.servicePeriodStatus}/>}
                    </>
                )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
                {intl.formatMessage({id: invoice.servicePeriodSource ? `expense.servicePeriodSource.${invoice.servicePeriodSource}` : 'view.incomingInvoice.servicePeriod.invoiceMonthHint'})}
            </p>
            {invoice.servicePeriodStatus === 'PROPOSED' && (
                <div className="mt-3">
                    <ActionButton
                        additionalClassname="ml-0!"
                        icon={faCheck}
                        label={intl.formatMessage({id: 'view.incomingInvoice.servicePeriod.confirm'})}
                        isSubmitting={isSubmitting}
                        onClick={confirm}
                    />
                </div>
            )}
            <form onSubmit={correct} className="mt-4 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-gray-500">
                        {intl.formatMessage({id: 'view.incomingInvoice.servicePeriod.start'})}
                        <input type="date" value={start} onChange={event => setStart(event.target.value)} className={dateInputClassName} required/>
                    </label>
                    <label className="text-xs text-gray-500">
                        {intl.formatMessage({id: 'view.incomingInvoice.servicePeriod.end'})}
                        <input type="date" value={end} min={start || undefined} onChange={event => setEnd(event.target.value)} className={dateInputClassName} required/>
                    </label>
                </div>
                {start !== '' && end !== '' && end < start && (
                    <p className="text-xs text-red-600">{intl.formatMessage({id: 'view.incomingInvoice.servicePeriod.endBeforeStart'})}</p>
                )}
                <ActionButton
                    type="submit"
                    additionalClassname="ml-0!"
                    label={intl.formatMessage({id: servicePeriod === null ? 'view.incomingInvoice.servicePeriod.set' : 'view.incomingInvoice.servicePeriod.correct'})}
                    disabled={!isChanged || !isValid}
                    isSubmitting={isSubmitting}
                />
            </form>
        </Panel>
    );
};

export default ExpenseInvoiceServicePeriod;
